import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

/**
 * Dashboard Oficina (at-86) — a operação da oficina.
 *
 * ⚠️ Não confundir com `api/contac/contacDashboardApi.ts`: aquele lê a carteira da ASSOCIAÇÃO
 * (arrecadação, frota protegida) do banco do Contac. Este lê o banco da Oficina e só conhece
 * veículos que estão ou já estiveram lá.
 */

export type SituacaoAtendimento = 'na-oficina' | 'entregues' | 'ambos';

/**
 * Tipo de dano do atendimento (at-88).
 *
 * ⚠️ `NAO_INFORMADO` não é valor do cadastro — é o recorte "o campo ficou em branco", que precisa
 * existir porque boa parte da base está sem ele. Sem essa opção, filtrar os três tamanhos daria um
 * total menor que o da tela sem filtro, e o usuário não teria como alcançar a diferença.
 */
export type FiltroTipoDeDano = 'PEQUENO' | 'MEDIO' | 'GRANDE' | 'NAO_INFORMADO';

export const TIPOS_DE_DANO: Array<{ valor: FiltroTipoDeDano; rotulo: string }> = [
  { valor: 'PEQUENO', rotulo: 'Pequeno' },
  { valor: 'MEDIO', rotulo: 'Médio' },
  { valor: 'GRANDE', rotulo: 'Grande' },
  { valor: 'NAO_INFORMADO', rotulo: 'Não informado' },
];

export interface FiltrosOficina {
  situacao?: SituacaoAtendimento;
  dataInicio?: string;
  dataFim?: string;
  esteiraId?: string;
  carClass?: string;
  damageType?: FiltroTipoDeDano;
}

export interface JanelaOficina {
  dataInicio: string;
  dataFim: string;
  situacao: SituacaoAtendimento;
  /** Antes disto houve a carga inicial do go-live — a tela avisa quando o período a inclui. */
  inicioOperacaoReal: string;
}

export interface RespostaVisaoGeral {
  janela: JanelaOficina;
  kpis: {
    naOficinaAgora: number;
    entreguesNoPeriodo: number;
    permanenciaMediaDias: number;
    aguardandoLiberacao: number;
    atendimentosComRetorno: number;
    prazoEmRisco: number;
  };
  porEsteira: Array<{ esteiraId: string; esteira: string; veiculos: number; diasMedios: number }>;
  fluxoMensal: Array<{
    mes: string;
    entradas: number;
    entregas: number;
    placasEntradas: string[];
    placasEntregas: string[];
  }>;
  mixPorClasse: Array<{ classe: string; quantidade: number }>;
  /** Placas por trás de cada indicador — alimentam o detalhamento por clique. */
  placasPorIndicador: {
    naOficinaAgora: string[];
    entreguesNoPeriodo: string[];
    permanenciaMediaDias: string[];
    aguardandoLiberacao: string[];
    atendimentosComRetorno: string[];
    prazoEmRisco: string[];
  };
  atendimentos: AtendimentoResumo[];
}

export interface RespostaSla {
  janela: JanelaOficina;
  kpis: {
    horasTrabalhadas: number;
    sessoesFinalizadas: number;
    colaboradoresAtivos: number;
    horasAguardandoAprovacao: number;
    servicosInacabados: number;
    percentualPausa: number;
    horasPausa: number;
  };
  porEsteira: Array<{
    esteiraId: string;
    esteira: string;
    permanenciaMediaHoras: number;
    /** Homem-hora TOTAL da esteira — responde "qual etapa consome mais mão de obra". */
    horasTrabalhadas: number;
    trabalhoMedioHoras: number;
    passagens: number;
    sessoes: number;
  }>;
  porColaborador: Array<{
    colaboradorId: string;
    colaborador: string;
    horasTrabalhadas: number;
    sessoes: number;
    mediaHorasPorSessao: number;
    percentualPausa: number;
    horasPausa: number;
  }>;
  matrizColaboradorEsteira: Array<{
    colaboradorId: string;
    colaborador: string;
    esteiraId: string;
    esteira: string;
    horasTrabalhadas: number;
    sessoes: number;
    mediaHorasPorSessao: number;
  }>;
  cumprimentoSla: Array<{
    esteiraId: string;
    esteira: string;
    dentroDoPrazo: number;
    foraDoPrazo: number;
    semPrazoDefinido: number;
    /** A linha é uma PASSAGEM: veículo que voltou à mesma esteira aparece mais de uma vez. */
    passagens: Array<{
      plate: string;
      diasNaEsteira: number;
      prazoPrevistoDias: number | null;
      situacao: 'DENTRO' | 'FORA' | 'SEM_PRAZO';
    }>;
  }>;
  /**
   * As três origens de pausa, separadas: "fora do expediente" é a oficina FECHADA e não conta
   * como pausa de ninguém — some-a às outras e o indicador vira medida de esquecimento.
   */
  pausas: { manualHoras: number; janelaHoras: number; foraExpedienteHoras: number };
}

/**
 * ⚠️ Só entram no envio os filtros PREENCHIDOS.
 *
 * O DTO do backend recusa parâmetro desconhecido e valor fora do formato; mandar `situacao: ''`
 * ou `dataInicio: undefined` viraria 400 numa tela que o usuário abriu sem escolher nada.
 */
const montarParams = (filtros: FiltrosOficina): Record<string, string> => {
  const params: Record<string, string> = {};
  if (filtros.situacao) params.situacao = filtros.situacao;
  if (filtros.dataInicio) params.dataInicio = filtros.dataInicio;
  if (filtros.dataFim) params.dataFim = filtros.dataFim;
  if (filtros.esteiraId) params.esteiraId = filtros.esteiraId;
  if (filtros.carClass) params.carClass = filtros.carClass;
  if (filtros.damageType) params.damageType = filtros.damageType;
  return params;
};

const cabecalho = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

export const buscarVisaoGeralOficina = async (
  filtros: FiltrosOficina,
): Promise<RespostaVisaoGeral> => {
  const { data } = await axios.get<RespostaVisaoGeral>(`${BACKEND_URL}/dashboard-oficina/visao-geral`, {
    params: montarParams(filtros),
    headers: cabecalho(),
  });
  return data;
};

export const buscarSlaOficina = async (filtros: FiltrosOficina): Promise<RespostaSla> => {
  const { data } = await axios.get<RespostaSla>(`${BACKEND_URL}/dashboard-oficina/sla`, {
    params: montarParams(filtros),
    headers: cabecalho(),
  });
  return data;
};

/** Situação do veículo diante do prazo da etapa em que ele está agora (at-88). */
export type SituacaoPrazo = 'DENTRO' | 'EM_RISCO' | 'ESTOURADO' | 'SEM_PRAZO';

/** Motivo aparente de o veículo não receber trabalho — indício, nunca diagnóstico fechado. */
export type MotivoParado =
  | 'SEM_LIBERACAO'
  | 'SEM_COLABORADOR'
  | 'SERVICO_INACABADO'
  | 'SESSAO_PAUSADA'
  | 'SEM_MOTIVO_APARENTE';

export interface VeiculoPrazo {
  plate: string;
  model: string | null;
  esteira: string;
  esteiraId: string;
  diasNaEtapa: number;
  prazoDaEtapa: number | null;
  diasAlemDoPrazo: number | null;
  situacao: SituacaoPrazo;
  prazoEntregaRestante: number | null;
  diasNaOficina: number;
}

export interface VeiculoParado {
  plate: string;
  model: string | null;
  esteira: string;
  diasSemAtividade: number;
  motivo: MotivoParado;
}

export interface RespostaPrazos {
  /** Instante da leitura — esta aba descreve o pátio agora, não um período. */
  referencia: string;
  /** Filtros da tela que esta aba ignora de propósito; a faixa de aviso sai daqui. */
  filtrosNaoAplicados: string[];
  diasParaConsiderarParado: number;
  kpis: {
    estouradosAgora: number;
    emRiscoEtapa: number;
    prazoEntregaEmRisco: number;
    semPrazoDefinido: number;
    paradosSemAtividade: number;
  };
  porEsteira: Array<{
    esteiraId: string;
    esteira: string;
    dentroDoPrazo: number;
    emRisco: number;
    estourado: number;
    semPrazo: number;
    placas: string[];
  }>;
  veiculos: VeiculoPrazo[];
  parados: VeiculoParado[];
  placasPorIndicador: {
    estouradosAgora: string[];
    emRiscoEtapa: string[];
    prazoEntregaEmRisco: string[];
    semPrazoDefinido: string[];
    paradosSemAtividade: string[];
  };
}

export const buscarPrazosOficina = async (filtros: FiltrosOficina): Promise<RespostaPrazos> => {
  const { data } = await axios.get<RespostaPrazos>(`${BACKEND_URL}/dashboard-oficina/prazos`, {
    params: montarParams(filtros),
    headers: cabecalho(),
  });
  return data;
};

/** Um atendimento na lista de apoio — resolve o detalhamento por placa dos gráficos. */
export interface AtendimentoResumo {
  plate: string;
  model: string | null;
  carClass: string;
  esteira: string;
  naOficina: boolean;
  diasNaOficina: number;
  noSga: boolean;
  categoria: string | null;
  marca: string | null;
  anoModelo: number | null;
  idadeAnos: number | null;
  valorProtegido: number | null;
  consultor: string | null;
  cooperativa: string | null;
  bairro: string | null;
  cidade: string | null;
  faixaEtaria: string | null;
  tempoDeAssociacao: string | null;
  situacaoVeiculo: string | null;
  situacaoAssociado: string | null;
  /** Tipo de dano do cadastro (at-88) — a coluna do recorte, quando o filtro de dano é usado. */
  damageType: string | null;
  retornos: number;
  prazoRestante: number | null;
  horasTrabalhadas: number;
  /** Dias na esteira ATUAL — é o número do gráfico "Tempo médio na esteira atual". */
  diasNaEsteiraAtual: number;
}

/** Bloco de indicadores da frota — a tela mostra dois: o do recorte e o da oficina agora. */
export interface KpisFrota {
  veiculos: number;
  localizados: number;
  valorProtegido: number;
  valorMedioPorVeiculo: number;
  idadeMediaAnos: number;
  placas: string[];
}

export interface FatiaComPlacas {
  rotulo: string;
  quantidade: number;
  placas: string[];
}

/**
 * Quantas placas do recorte foram localizadas no espelho do SGA.
 *
 * ⚠️ É CONTRATO, não detalhe: todo gráfico enriquecido é feito só sobre as localizadas, e sem
 * declarar isso na tela um gráfico montado sobre metade dos veículos passa por retrato do todo.
 */
export interface CoberturaSga {
  atendimentos: number;
  localizados: number;
  naoLocalizados: number;
  naoLocalizadosTerceiros: number;
}

export interface RespostaFrotaPerfil {
  janela: JanelaOficina;
  cobertura: CoberturaSga;
  /** Segue o filtro da tela. */
  kpisRecorte: KpisFrota;
  /** O que está na oficina agora, ignorando o filtro de propósito. */
  kpisNaOficina: KpisFrota;
  porCategoria: FatiaComPlacas[];
  porMarca: FatiaComPlacas[];
  idadeFrota: FatiaComPlacas[];
  faixaEtariaAssociados: FatiaComPlacas[];
  tempoDeAssociacao: FatiaComPlacas[];
  porCooperativa: FatiaComPlacas[];
  porConsultor: Array<{
    rotulo: string;
    quantidade: number;
    placas: string[];
    carteira: number;
  }>;
  custoPorCategoria: Array<{ rotulo: string; horas: number; horasMedias: number; veiculos: number; placas: string[] }>;
  custoPorIdade: Array<{ rotulo: string; horas: number; horasMedias: number; veiculos: number; placas: string[] }>;
  cadastroNaoAtivo: Array<{ plate: string; situacaoVeiculo: string; situacaoAssociado: string }>;
  atendimentos: AtendimentoResumo[];
}

export interface RespostaRegiao {
  janela: JanelaOficina;
  cobertura: CoberturaSga;
  porBairro: Array<{
    rotulo: string;
    cidade: string;
    quantidade: number;
    placas: string[];
    /** Tempo na oficina — inclui os que ainda estão lá, contados até hoje. */
    tempoNaOficinaMediaDias: number;
    frotaProtegida: number;
  }>;
  porCidade: FatiaComPlacas[];
  atendimentos: AtendimentoResumo[];
}

export const buscarFrotaPerfil = async (filtros: FiltrosOficina): Promise<RespostaFrotaPerfil> => {
  const { data } = await axios.get<RespostaFrotaPerfil>(
    `${BACKEND_URL}/dashboard-oficina/frota-perfil`,
    { params: montarParams(filtros), headers: cabecalho() },
  );
  return data;
};

export const buscarRegiaoOficina = async (filtros: FiltrosOficina): Promise<RespostaRegiao> => {
  const { data } = await axios.get<RespostaRegiao>(`${BACKEND_URL}/dashboard-oficina/regiao`, {
    params: montarParams(filtros),
    headers: cabecalho(),
  });
  return data;
};

/**
 * Retrabalho (at-133): dos atendimentos ENTREGUES, quantos voltaram em garantia (pós-evento).
 *
 * ⚠️ `percentual` é `null` quando não houve entrega no recorte — nunca 0, que seria lido como
 * "ninguém voltou". A tela mostra traço.
 */
export interface RespostaRetrabalho {
  janela: JanelaOficina;
  /** `['situacao']` quando a tela pediu outra situação: o card olha só entregues. */
  filtrosNaoAplicados: string[];
  totalServicos: number;
  servicosComRetorno: number;
  percentual: number | null;
  porEsteira: Array<{
    esteiraId: string;
    esteira: string;
    servicos: number;
    comRetorno: number;
    percentual: number | null;
  }>;
  retornos: Array<{
    placa: string;
    modelo: string | null;
    servicoEntregueEm: string | null;
    voltouEm: string;
    diasAteVoltar: number | null;
    esteiras: string[];
  }>;
  totalRetornos: number;
  listaCortada: boolean;
}

export const buscarRetrabalhoOficina = async (
  filtros: FiltrosOficina,
): Promise<RespostaRetrabalho> => {
  const { data } = await axios.get<RespostaRetrabalho>(
    `${BACKEND_URL}/dashboard-oficina/retrabalho`,
    { params: montarParams(filtros), headers: cabecalho() },
  );
  return data;
};

export type CausadorDoEvento = 'Associado' | 'Terceiro' | 'Roubo ou furto' | 'Sem informação';

export interface FatiaDeEvento extends FatiaComPlacas {
  percentual: number;
  tempoNaOficinaMediaDias: number;
}

/**
 * Aba Eventos (at-133): o atendimento cruzado com o evento do SGA que o originou.
 *
 * ⚠️ `limites` é parte da resposta e a tela o escreve por extenso: o espelho cobre só a base RJ e
 * eventos cadastrados desde 2025. Atendimento fora disso não "não teve evento" — o sistema não sabe.
 */
export interface RespostaEventos {
  janela: JanelaOficina;
  cobertura: { atendimentos: number; comEvento: number; semEvento: number };
  limites: {
    bases: string[];
    cadastradosDesde: string;
    ultimaSincronizacao: string | null;
    espelhoIndisponivel: boolean;
  };
  porMotivo: FatiaDeEvento[];
  porCausador: FatiaDeEvento[];
  porMes: Array<{ mes: string; quantidade: number }>;
  motivoPorCausador: Array<{
    motivo: string;
    associado: number;
    terceiro: number;
    /** Opcional só pela janela de deploy (backend anterior não envia). */
    rouboOuFurto?: number;
    semInformacao: number;
  }>;
  atendimentos: AtendimentoResumo[];
}

export const buscarEventosOficina = async (filtros: FiltrosOficina): Promise<RespostaEventos> => {
  const { data } = await axios.get<RespostaEventos>(`${BACKEND_URL}/dashboard-oficina/eventos`, {
    params: montarParams(filtros),
    headers: cabecalho(),
  });
  return data;
};

/**
 * Assistente do painel (at-87) — pergunta em linguagem natural sobre estes mesmos números.
 *
 * ⚠️ A conversa é STATELESS: o histórico vive na tela e vai inteiro a cada pergunta. Não há
 * sessão no servidor, e fechar o painel descarta o histórico.
 */
export interface MensagemDaConversa {
  papel: 'usuario' | 'assistente';
  texto: string;
}

/** O que foi consultado para chegar à resposta — a tela mostra como rodapé de transparência. */
export interface ConsultaFeita {
  ferramenta: string;
  recorte: FiltrosOficina;
}

export interface RespostaDoAssistente {
  texto: string;
  consultas: ConsultaFeita[];
  /** Preenchido quando a resposta saiu degradada (truncada, recusada ou sem conclusão). */
  aviso?: string;
}

export interface StatusDoAssistente {
  configurado: boolean;
  modelo: string | null;
}

export const consultarStatusAssistente = async (): Promise<StatusDoAssistente> => {
  const { data } = await axios.get<StatusDoAssistente>(
    `${BACKEND_URL}/dashboard-oficina/assistente/status`,
    { headers: cabecalho() },
  );
  return data;
};

export const perguntarAoAssistente = async (
  mensagens: MensagemDaConversa[],
  filtros: FiltrosOficina,
): Promise<RespostaDoAssistente> => {
  const { data } = await axios.post<RespostaDoAssistente>(
    `${BACKEND_URL}/dashboard-oficina/assistente`,
    { mensagens, filtros: montarParams(filtros) },
    { headers: cabecalho() },
  );
  return data;
};
